import React from 'react';
import PropTypes from 'prop-types';
import CustomizationSection from './CustomizationSection';
import { availableAddableExtras, removableIngredients } from './menuData';

/**
 * TwoFlavorModal
 * Gestisce la creazione di una pizza Due Gusti (normale o Maxi).
 * FIX: step 1 ora mostra anch'esso la CustomizationSection (era assente nell'originale).
 */
const TwoFlavorModal = ({
  isMaxi,
  step,
  setStep,
  firstHalf,
  setFirstHalf,
  secondHalf,
  setSecondHalf,
  pizzaList,
  isItemAvailable,
  onFinalize,
  onClose,
}) => {
  const accentColor = isMaxi ? 'red-800' : 'purple-600';
  const accentHover = isMaxi ? 'red-900' : 'purple-700';
  const title = isMaxi ? 'Crea Pizza Maxi Due Gusti 🍕🍕' : 'Crea Pizza Due Gusti 🍕🍕';

  const currentHalf = step === 1 ? firstHalf : secondHalf;
  const setCurrentHalf = step === 1 ? setFirstHalf : setSecondHalf;
  const stepLabel = step === 1 ? 'Prima Metà' : 'Seconda Metà';

  const handlePizzaSelect = (e) => {
    const selected = pizzaList.find((p) => p.id === e.target.value);
    setCurrentHalf({ selectedPizza: selected || null, additions: [], removals: [], cost: 0 });
  };

  const handleAddAddition = (name, price) => {
    setCurrentHalf((prev) => ({ ...prev, additions: [...prev.additions, { name, price }], cost: prev.cost + price }));
  };

  const handleRemoveAddition = (index) => {
    setCurrentHalf((prev) => {
      const newAdditions = prev.additions.filter((_, i) => i !== index);
      return { ...prev, additions: newAdditions, cost: newAdditions.reduce((s, m) => s + m.price, 0) };
    });
  };

  const handleAddRemoval = (name) => {
    setCurrentHalf((prev) => ({ ...prev, removals: [...prev.removals, name] }));
  };

  const handleRemoveRemoval = (index) => {
    setCurrentHalf((prev) => ({ ...prev, removals: prev.removals.filter((_, i) => i !== index) }));
  };

  const canProceed =
    currentHalf.selectedPizza && isItemAvailable(currentHalf.selectedPizza.id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl overflow-y-auto max-h-[90vh]">

        <h3 className={`text-2xl font-bold mb-1 text-${accentColor}`}>{title}</h3>
        <p className="text-sm text-gray-500 mb-4">— {stepLabel}</p>

        {/* Selezione pizza */}
        <div className="mb-4 border p-4 rounded-lg bg-gray-50">
          <h4 className="text-lg font-semibold mb-2 text-gray-900">
            Seleziona la {step === 1 ? 'prima' : 'seconda'} metà{isMaxi ? ' Maxi' : ''}:
          </h4>
          <select
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
            value={currentHalf.selectedPizza ? currentHalf.selectedPizza.id : ''}
            onChange={handlePizzaSelect}
          >
            <option value="">Scegli un gusto</option>
            {pizzaList.map((pizza) => (
              <option key={pizza.id} value={pizza.id} disabled={!isItemAvailable(pizza.id)}>
                {pizza.name} (€{pizza.price.toFixed(2)}){!isItemAvailable(pizza.id) ? ' – Non Disponibile' : ''}
              </option>
            ))}
          </select>
          {currentHalf.selectedPizza && (
            <p className="text-gray-600 text-sm mt-2">{currentHalf.selectedPizza.description}</p>
          )}
        </div>

        {/* Personalizzazione – mostrata su ENTRAMBI gli step (FIX) */}
        {currentHalf.selectedPizza && (
          <CustomizationSection
            itemBase={currentHalf.selectedPizza}
            currentAdditions={currentHalf.additions}
            currentRemovals={currentHalf.removals}
            onAddAddition={handleAddAddition}
            onRemoveAddition={handleRemoveAddition}
            onAddRemoval={handleAddRemoval}
            onRemoveRemoval={handleRemoveRemoval}
            availableAddableExtras={availableAddableExtras}
            removableIngredients={removableIngredients}
          />
        )}

        {/* Pulsanti navigazione */}
        <div className="flex justify-between items-center mt-6">
          {step === 1 ? (
            <button
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-5 py-2 rounded-md shadow-sm"
            >
              Annulla
            </button>
          ) : (
            <button
              onClick={() => setStep(1)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-5 py-2 rounded-md shadow-sm"
            >
              ← Indietro
            </button>
          )}

          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              disabled={!canProceed}
              className={`bg-${accentColor} hover:bg-${accentHover} text-white px-5 py-2 rounded-md shadow-sm disabled:opacity-40`}
            >
              Successivo →
            </button>
          ) : (
            <button
              onClick={onFinalize}
              disabled={!canProceed}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md shadow-sm disabled:opacity-40"
            >
              Conferma e Aggiungi al Carrello
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

TwoFlavorModal.propTypes = {
  isMaxi: PropTypes.bool.isRequired,
  step: PropTypes.number.isRequired,
  setStep: PropTypes.func.isRequired,
  firstHalf: PropTypes.object.isRequired,
  setFirstHalf: PropTypes.func.isRequired,
  secondHalf: PropTypes.object.isRequired,
  setSecondHalf: PropTypes.func.isRequired,
  pizzaList: PropTypes.array.isRequired,
  isItemAvailable: PropTypes.func.isRequired,
  onFinalize: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,

};

export default TwoFlavorModal;
